import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { acAPI } from "../../lib/academicApi";
import { PageHeader, Loading, Section, StatusPill, EmptyState } from "../../components/Common";
import { ArrowLeft, ClipboardCheck, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const STAGES = ["hod_review", "deputy_review", "principal_review", "approved", "rejected"];

export default function Moderation() {
  const { school } = useParams();
  const nav = useNavigate();
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("");

  const load = () => acAPI.listModeration(filter).then(({ data }) => setItems(data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const advance = async (m, next) => {
    const note = prompt(`Note for stage ${next}?`) || "";
    try { await acAPI.advanceModeration(m.id, { stage: next, note }); toast.success("Advanced"); load(); }
    catch { toast.error("Failed"); }
  };

  if (items === null) return <Loading />;

  return (
    <div data-testid="moderation">
      <PageHeader
        kicker="Academic360™ · Moderation360"
        title="Moderation queue"
        subtitle="HoD → Deputy → Principal sign-off. Full audit history retained."
        actions={<button onClick={() => nav(`/education360/${school}/academic`)} className="hl-button-secondary text-xs"><ArrowLeft className="w-3 h-3 inline mr-1" />Hub</button>}
      />
      <div className="hl-card p-3 mb-4 flex gap-2 items-center" data-testid="filter">
        <span className="text-xs text-slate-500">Stage</span>
        <select value={filter} onChange={e => setFilter(e.target.value)} className="border border-slate-200 rounded px-2 py-1 text-sm">
          <option value="">All</option>
          {STAGES.map(s => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
        </select>
      </div>

      {items.length === 0 && <EmptyState title="Moderation queue is empty" body="Assessments submitted for moderation will appear here." />}

      <div className="space-y-3" data-testid="mod-list">
        {items.map(m => {
          const nextStage = {
            hod_review: "deputy_review",
            deputy_review: "principal_review",
            principal_review: "approved",
          }[m.stage];
          return (
            <div key={m.id} className="hl-card p-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs text-slate-500 mono">Assessment {m.assessment_id.slice(0, 8)}</div>
                  <div className="font-semibold text-slate-800">Submitted by {m.submitted_by}</div>
                </div>
                <StatusPill status={m.stage} />
              </div>
              <div className="mt-3 flex flex-wrap gap-2 text-xs">
                {(m.audit || []).map((a, i) => (
                  <span key={i} className="bg-slate-100 text-slate-700 px-2 py-1 rounded">
                    {a.stage} · {a.by} · {a.at?.slice(0, 10)}
                  </span>
                ))}
              </div>
              {m.stage !== "approved" && m.stage !== "rejected" && nextStage && (
                <div className="mt-3 flex gap-2">
                  <button onClick={() => advance(m, nextStage)} className="hl-button-primary text-xs" data-testid={`advance-${m.id}`}>
                    Advance to {nextStage.replace(/_/g, " ")} <ArrowRight className="w-3 h-3 inline ml-1" />
                  </button>
                  <button onClick={() => advance(m, "rejected")} className="hl-button-secondary text-xs">Reject</button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
